import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import set from 'lodash/set';
import { SuiTextInput } from '@splashthat/sui';
import ColorPickerEditor from 'common/components/ColorPicker';
import ImagePicker from 'common/components/ImagePicker';
import Spacer from 'common/components/Spacer';

import { useNode } from '@splashthat/splash-cms-engine';

const TRANSPARENT_FALLBACK = { value: 'transparent' };
const ALT_TEXT_LABEL = 'Alt Text';

export default function BackgroundImageTools({
    colors,
    backgroundColorPath,
    backgroundUrlPath,
    originalBackgroundUrlPath,
    accessibilityTextPath,
    colorAriaLabel,
    qaId,
}) {
    const node = useNode();
    const colorList = colors.map((color, index) => ({
        id: index,
        value: color,
    }));

    const onUpdateColor = (id) =>
        node.actions.setProp((editedNode) => {
            set(editedNode, backgroundColorPath, id);
            return editedNode;
        });

    const onChangeImage = (baseUrl) =>
        node.actions.setProp((editedNode) => {
            set(editedNode, originalBackgroundUrlPath, baseUrl);
            set(editedNode, backgroundUrlPath, baseUrl);
            !baseUrl && set(editedNode, accessibilityTextPath, '');
            return editedNode;
        });

    const onEditImage = (url) =>
        node.actions.setProp((editedNode) => {
            set(editedNode, backgroundUrlPath, url);
            return editedNode;
        });

    const onChangeAccessibilityText = (_, accessibilityText) =>
        node.actions.setProp((editedNode) => {
            set(editedNode, accessibilityTextPath, accessibilityText);
            return editedNode;
        });

    return (
        <React.Fragment>
            <ColorPickerEditor
                colorList={colorList}
                firstColor={colorList[get(node, backgroundColorPath)] || TRANSPARENT_FALLBACK}
                changeFirstColor={onUpdateColor}
                aria-label={colorAriaLabel}
                qaId={qaId}
            />
            <Spacer />
            <ImagePicker
                imageUrl={get(node, backgroundUrlPath)}
                imageBaseUrl={get(node, originalBackgroundUrlPath) || get(node, backgroundUrlPath)}
                onChangeImage={onChangeImage}
                onEditImage={onEditImage}
                qaId={qaId}
            />
            {!!get(node, backgroundUrlPath) && (
                <SuiTextInput
                    label={ALT_TEXT_LABEL}
                    value={node.meta.accessibilityText}
                    onChange={onChangeAccessibilityText}
                    data-qa={`${qaId}__accessibility-text-input`}
                    fullWidth
                />
            )}
        </React.Fragment>
    );
}

BackgroundImageTools.propTypes = {
    colors: PropTypes.array.isRequired,
    backgroundColorPath: PropTypes.string,
    backgroundUrlPath: PropTypes.string,
    originalBackgroundUrlPath: PropTypes.string,
    accessibilityTextPath: PropTypes.string,
    colorAriaLabel: PropTypes.string,
    qaId: PropTypes.string,
};

BackgroundImageTools.defaultProps = {
    backgroundColorPath: 'styles.backgroundColor',
    backgroundUrlPath: 'styles.backgroundImageUrl',
    originalBackgroundUrlPath: 'styles.originalBackgroundImageUrl',
    accessibilityTextPath: 'meta.accessibilityText',
    colorAriaLabel: '',
    qaId: '',
};
